import React from "react";
import { ParameterSchemaStation } from "../../API/Schema/ParameterSchema";
import { MainSite } from "../../MainSite";
import { createTheme, ThemeProvider } from "@mui/material";
import { ProfileComponent } from "../../ValueTypes/Profile";
import { SpeedUnits } from "../SiteObservations/Current";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { DataContext } from "../../Contexts/dataContext";

interface Props {
    station: ParameterSchemaStation;
    profileName: string;
    parameterGroup: string;
    profileEnumType: ProfileTypeEnum;
    speedUnits: SpeedUnits;
    unitsStrDepthOrHeight: string;
    ucf_DepthOrHeight: (value: any) => any;
    speedUnitsStr: string;
    ucf_Speed: (value: any) => any;
}
export enum ProfileTypeEnum {
    Depth,
    Height,
    Horizontal,
}

export function ProfileChartComponent(props: Props): React.JSX.Element {
    const theme = createTheme({
        //: https://mui.com/material-ui/customization/typography/#font-size  +  https://mui.com/material-ui/customization/theme-components/#theme-variables
        typography: {
            fontSize: 20,
        },
    });

    const latestData = React.useContext(DataContext);

    //// If data not available
    if (
        !latestData ||
        Object.keys(latestData.Stations).length == 0 ||
        !latestData?.Stations[props.station.Station.Id]?.GroupValues[props.parameterGroup] ||
        !latestData?.Stations[props.station.Station.Id]?.GroupValues[props.parameterGroup][0]
            ?.Value?.Components ||
        latestData?.Stations[props.station.Station.Id]?.GroupValues[props.parameterGroup][0]?.Value
            ?.Components.length == 0
    ) {
        return (
            <React.StrictMode>
                <ThemeProvider theme={theme}>
                    <div className="midi_SiteObservations_chart_no_data">Data not available</div>
                </ThemeProvider>
            </React.StrictMode>
        );
    }

    return <ProfileChartComponentInner {...props} />;
}

export function ProfileChartComponentInner(props: Props): React.JSX.Element {
    const theme = createTheme({
        //: https://mui.com/material-ui/customization/typography/#font-size  +  https://mui.com/material-ui/customization/theme-components/#theme-variables
        typography: {
            fontSize: 20,
        },
    });

    const latestData = React.useContext(DataContext);

    const chartContainerRef = React.useRef(undefined);
    const chartRef = React.useRef<__Highcharts.ChartObject>(undefined);

    const profileData = latestData.Stations[props.station.Station.Id].GroupValues[
        props.parameterGroup
    ][0] as any;

    //// SET UP THE CHART
    let seriesOptions = [];

    let lowerXAxisData = []; // Speed axis
    let lowerXAxisDataMin = [];
    let lowerXAxisDataMax = [];

    let upperXAxisData = []; // Direction axis
    let upperXAxisDataMin = [];
    let upperXAxisDataMax = [];

    let speedUnitsStr = props.speedUnits == SpeedUnits.mps ? "m/s" : "kts";

    let unitsConversionSet_DepthOrHeight: IUnitsConversionSet | null =
        MainSite.getUnitConverter().getConversionSet(props.unitsStrDepthOrHeight);
    let unitsConversionSet_Speed: IUnitsConversionSet | null =
        MainSite.getUnitConverter().getConversionSet(speedUnitsStr);
    let ucf_DepthOrHeight: (value: any) => any = (value: any) => value;
    let ucf_Speed: (value: any) => any = (value: any) => value;
    if (unitsConversionSet_DepthOrHeight) {
        ucf_DepthOrHeight = unitsConversionSet_DepthOrHeight.convertValue;
        props.unitsStrDepthOrHeight = unitsConversionSet_DepthOrHeight.convertedUnits;
    }
    if (unitsConversionSet_Speed) {
        ucf_Speed = unitsConversionSet_Speed.convertValue;
        speedUnitsStr = unitsConversionSet_Speed.convertedUnits;
    }

    const getFormattedAxisData = (values, lowerAxis, upperAxis) => {
        values?.Components.forEach((component: ProfileComponent) => {
            if (props.profileEnumType === ProfileTypeEnum.Depth && component.Depth) {
                if (component.Direction) {
                    upperAxis.push([
                        component.Direction,
                        props.ucf_DepthOrHeight(component.Depth),
                    ]);
                }

                if (component.Speed) {
                    lowerAxis.push([
                        props.ucf_Speed(component.Speed),
                        props.ucf_DepthOrHeight(component.Depth),
                    ]);
                }
            }
            if (props.profileEnumType === ProfileTypeEnum.Height && component.Height) {
                if (component.Direction) {
                    upperAxis.push([
                        component.Direction,
                        props.ucf_DepthOrHeight(component.Height),
                    ]);
                }

                if (component.Speed) {
                    lowerAxis.push([
                        props.ucf_Speed(component.Speed),
                        props.ucf_DepthOrHeight(component.Height),
                    ]);
                }
            }
            if (
                props.profileEnumType === ProfileTypeEnum.Horizontal &&
                props.ucf_DepthOrHeight(component.Depth)
            ) {
                if (component.Direction) {
                    upperAxis.push([
                        props.ucf_DepthOrHeight(component.Depth),
                        component.Direction,
                    ]);
                }

                if (component.Speed) {
                    lowerAxis.push([
                        props.ucf_DepthOrHeight(component.Depth),
                        props.ucf_Speed(component.Speed),
                    ]);
                }
            }
        });

        return [lowerAxis, upperAxis];
    };

    [lowerXAxisData, upperXAxisData] = getFormattedAxisData(
        profileData.Value,
        lowerXAxisData,
        upperXAxisData,
    );
    [lowerXAxisDataMin, upperXAxisDataMin] = getFormattedAxisData(
        profileData.ValueMin,
        lowerXAxisDataMin,
        upperXAxisDataMin,
    );
    [lowerXAxisDataMax, upperXAxisDataMax] = getFormattedAxisData(
        profileData.ValueMax,
        lowerXAxisDataMax,
        upperXAxisDataMax,
    );

    let xAxisOptions: __Highcharts.AxisOptions[] = [];
    let yAxisOptions: __Highcharts.AxisOptions[] = [];

    seriesOptions.push(
        //// SPEED
        {
            name: "Speed",
            color: "rgba(255, 0, 0, 1)",
            data: lowerXAxisData,
            xAxis: 0,
            yAxis: 0,
            dashStyle: "longdash",
            marker: {
                enabled: true,
                symbol: "circle",
            },
            tooltip: {
                headerFormat: "<b>{series.name}</b><br>",
                pointFormatter:
                    props.profileEnumType === ProfileTypeEnum.Horizontal
                        ? function () {
                              return `${this.y.toFixed(1)} ${props.speedUnitsStr} at ${this.x.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          }
                        : function () {
                              return `${this.x.toFixed(1)} ${props.speedUnitsStr} at ${this.y.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          },
            },
        },
        {
            name: "Min Speed",
            color: "rgba(255, 0, 0, 0.4)",
            data: lowerXAxisDataMin,
            xAxis: 0,
            yAxis: 0,
            dashStyle: "longdash",
            marker: {
                enabled: true,
                symbol: "circle",
            },
            tooltip: {
                headerFormat: "<b>{series.name}</b><br>",
                pointFormatter:
                    props.profileEnumType === ProfileTypeEnum.Horizontal
                        ? function () {
                              return `${this.y.toFixed(1)} ${props.speedUnitsStr} at ${this.x.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          }
                        : function () {
                              return `${this.x.toFixed(1)} ${props.speedUnitsStr} at ${this.y.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          },
            },
        },
        {
            name: "Max Speed",
            color: "rgba(255, 0, 0, 0.4)",
            data: lowerXAxisDataMax,
            xAxis: 0,
            yAxis: 0,
            dashStyle: "longdash",
            marker: {
                enabled: true,
                symbol: "circle",
            },
            tooltip: {
                headerFormat: "<b>{series.name}</b><br>",
                pointFormatter:
                    props.profileEnumType === ProfileTypeEnum.Horizontal
                        ? function () {
                              return `${this.y.toFixed(1)} ${props.speedUnitsStr} at ${this.x.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          }
                        : function () {
                              return `${this.x.toFixed(1)} ${props.speedUnitsStr} at ${this.y.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          },
            },
        },
        //// DIRECTION
        {
            name: "Direction " + (props.parameterGroup.includes("Current") ? "To" : "From"),
            color: "rgba(0, 128, 0, 1)",
            data: upperXAxisData,
            xAxis: props.profileEnumType === ProfileTypeEnum.Horizontal ? 0 : 1,
            yAxis: props.profileEnumType === ProfileTypeEnum.Horizontal ? 1 : 0,
            lineWidth: 0,
            marker: {
                enabled: true,
                symbol: "diamond",
            },
            tooltip: {
                headerFormat: "<b>{series.name}</b><br>",
                pointFormatter:
                    props.profileEnumType === ProfileTypeEnum.Horizontal
                        ? function () {
                              return `${Math.round(this.y)}°T ${this.profileName === "Current" ? "TO" : "FROM"} at ${this.x.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          }
                        : function () {
                              return `${Math.round(this.x)}°T ${this.profileName === "Current" ? "TO" : "FROM"} at ${this.y.toFixed(1)} ${props.unitsStrDepthOrHeight}`;
                          },
            },
        },
    );

    //// CONFIGURE AXIS
    let speedAxisOptions: any = {
        title: {
            text: `Speed (${props.speedUnitsStr}) <span class="custom-actions actions--set-xaxis-range" style="color: blue; text-decoration: underline; cursor: pointer;">Custom Range</span>`,
            useHtml: true,
        },
        alignTicks: false,
    };

    let dirAxisOptions = {
        title: {
            text:
                "Direction (�T " +
                (props.parameterGroup.includes("Current") ? "TO" : "FROM") +
                ")",
        },
        min: 0,
        max: 360,
        opposite: true,
        tickInterval: 90,
        minTickInterval: 90,
        alignTicks: false,
    };

    const depthAxisOptions = {
        title: {
            text: `Depth (${props.unitsStrDepthOrHeight})`,
        },
        reversed: true,
    };

    const heightAxisOptions = {
        title: {
            text: `Height (${props.unitsStrDepthOrHeight})`,
        },
        reversed: false,
    };

    const distanceAxisOptions = {
        title: {
            text: `Distance (${props.unitsStrDepthOrHeight})`,
        },
        reversed: false,
    };

    if (props.profileEnumType !== ProfileTypeEnum.Horizontal) {
        xAxisOptions.push(speedAxisOptions);
        xAxisOptions.push(dirAxisOptions);
        yAxisOptions.push(
            props.profileEnumType === ProfileTypeEnum.Depth ? depthAxisOptions : heightAxisOptions,
        );
    } else {
        xAxisOptions.push(distanceAxisOptions);
        yAxisOptions.push(speedAxisOptions);
        yAxisOptions.push(dirAxisOptions);
    }

    //// CONFIGURE CHARTS
    const options = {
        chart: {
            type: "line",
            marginTop: 60,
        },
        exporting: {
            enabled: true,
        },
        title: {
            text: "",
        },
        xAxis: xAxisOptions,
        yAxis: yAxisOptions,
        legend: {
            enabled: true,
        },
        plotOptions: {
            line: {
                marker: {
                    radius: 5,
                    enabled: true,
                },
            },
            series: {
                animation: false,
                // Line series default to 'x' only, which picks the wrong point on vertical profiles.
                findNearestPointBy: "xy",
            },
        },
        series: seriesOptions,
        credits: {
            enabled: false,
        },
    };

    React.useEffect(() => {
        if (!chartRef.current) {
            chartRef.current = Highcharts.chart(chartContainerRef.current, options);
        } else {
            while (chartRef.current.series.length) {
                chartRef.current.series[0].remove();
            }
            for (let i = 0; i < seriesOptions.length; i++) {
                chartRef.current.addSeries(seriesOptions[i]);
            }
            chartRef.current.reflow();
        }
    });

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <div ref={chartContainerRef} style={{ height: "100%", width: "100%" }} />
            </ThemeProvider>
        </React.StrictMode>
    );
}
